import { useState } from "react";
import { useTranslation } from "react-i18next";
import MuiDrawer from "@mui/material/Drawer";
import Button from "../Button/Button";
import IconButton from "../IconButton/IconButton";
import TextField from "../TextField/TextField";
import TransparentButton from "../TransparentButton/TransparentButton";
import Grid from "../../config/Grid/Grid";
import { drawerStyles } from "../Drawer/Drawer.styles";
import drawerCss from "../Drawer/Drawer.module.scss";
import type { useDishFilters } from "@/hooks/useDishFilters";
import styles from "./DishFilterBar.module.scss";

interface DishFilterBarProps {
  filters: ReturnType<typeof useDishFilters>;
}

/**
 * Search field with the filters beside it, opening in a sheet.
 *
 * Ports the waiter app's filter bar. The sheet is a drawer rather than a route
 * of its own, because every route segment in this app is load-bearing, with
 * three components switching drawers off the pathname.
 */
function DishFilterBar(props: DishFilterBarProps) {
  const { filters } = props;
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const {
    filterGroups,
    toggle,
    mode,
    setMode,
    searchQuery,
    setSearchQuery,
    clearAll,
    activeFilterCount,
    hasActiveFilters,
  } = filters;

  return (
    <div className={styles.filterBar}>
      <div className={styles.searchRow}>
        <div className={styles.searchField}>
          <TextField
            id="dish-search"
            label={t("order.filters.search")}
            placeholder={t("order.filters.searchPlaceholder")}
            focused
            field={{
              name: "dish-search",
              value: searchQuery,
              onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
                setSearchQuery(event.target.value),
            }}
          />
        </div>

        {filterGroups.length > 0 ? (
          <IconButton
            iconName="las la-sliders-h icon-big"
            onClick={() => setOpen(true)}
            // Inverted while filters are on, so the button says so without a
            // count the icon has no room for.
            color={activeFilterCount > 0 ? "secondary" : "primary"}
          />
        ) : null}
      </div>

      <MuiDrawer
        // The shared paper scrolls as one block. Here it becomes a column so
        // the groups scroll on their own and the footer can sit ON the paper
        // rather than over it: laying the app's translucent bar surface on top
        // of an already translucent panel stacks the two into a dark box.
        sx={{
          ...drawerStyles,
          ".MuiPaper-root": {
            ...drawerStyles[".MuiPaper-root"],
            display: "flex",
            flexDirection: "column",
            overflowY: "hidden",
          },
        }}
        anchor="bottom"
        open={open}
        onClose={() => setOpen(false)}
      >
        <div className={styles.sheetBody}>
        <Grid>
          <div
            className={`start-1 col-4 align-start ${drawerCss.drawerLayoutWrapper}`}
          >
            <div className={drawerCss.drawerLayoutHeader}>
              <IconButton
                iconName="las la-angle-left icon-big"
                onClick={() => setOpen(false)}
                color="primary"
              />
              <p className={`body-bold-large ${drawerCss.drawerLayoutTitle}`}>
                {activeFilterCount > 0
                  ? `${t("order.filters.button")} (${activeFilterCount})`
                  : t("order.filters.button")}
              </p>
              <IconButton
                iconName=""
                onClick={() => {}}
                color="primary"
                variant="text"
              />
            </div>

            <div className={styles.filterPanel}>
              <div className={styles.filterGroup}>
                <p className={`body-bold-medium ${styles.filterGroupLabel}`}>
                  {t("order.filters.mode")}
                </p>
                <div className={styles.modeRow}>
                  <TransparentButton
                    text={t("order.filters.modeExclude")}
                    value="exclude"
                    selectedValue={mode}
                    setValue={() => setMode("exclude")}
                  />
                  <TransparentButton
                    text={t("order.filters.modeInclude")}
                    value="include"
                    selectedValue={mode}
                    setValue={() => setMode("include")}
                  />
                </div>
              </div>

              {filterGroups.map((group) => (
                <div key={group.key} className={styles.filterGroup}>
                  <p className={`body-bold-medium ${styles.filterGroupLabel}`}>
                    {group.label}
                  </p>
                  <div className={styles.chipRow}>
                    {group.options.map((option) => {
                      const isSelected = group.selected.includes(option.id);
                      return (
                        <div
                          key={option.id}
                          className={`${styles.chip} ${
                            isSelected ? styles.chipSelected : ""
                          }`}
                          onClick={() => toggle(group.key, option.id)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              toggle(group.key, option.id);
                            }
                          }}
                        >
                          {option.iconUrl ? (
                            <span
                              aria-hidden="true"
                              className={styles.chipIcon}
                              style={{
                                maskImage: `url("${option.iconUrl}")`,
                                WebkitMaskImage: `url("${option.iconUrl}")`,
                              }}
                            />
                          ) : null}
                          <p className={`body-bold-medium ${styles.chipLabel}`}>
                            {option.label}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}

            </div>
          </div>
        </Grid>
        </div>

        {/* Always on the sheet, so the bottom of the panel does not jump the
            moment a chip is tapped. It just wakes up when there is something
            to clear. */}
        <div className={styles.sheetFooter}>
          <Grid>
            <div className={`start-1 col-4 ${styles.sheetFooterInner}`}>
              <Button
                onClick={clearAll}
                label={t("order.filters.clear")}
                variant="outlined"
                color="primary"
                maxWidth
                mobile={true}
                disabled={!hasActiveFilters}
              />
            </div>
          </Grid>
        </div>
      </MuiDrawer>
    </div>
  );
}

export default DishFilterBar;
